import React from 'react';
import { i18n } from 'config/locales';
import { ForeignBusinessConferencesItemModel } from 'forms/sections/foreign/business/conferences/types';
import {
  CommonScrollIntoView, CommonRequired, CommonOnUpdate, CommonOnError
} from 'types/props';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import Country from 'components/Form/Country';
import DateRange from 'components/Form/DateRange';
import ConferenceContacts from './ConferenceContacts';

interface Props extends ValidationElementProps, ForeignBusinessConferencesItemModel {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
}

export default class ConferencesItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  update = (queue: Partial<ForeignBusinessConferencesItemModel>) => {
    this.props.onUpdate({
      Description: this.props.Description,
      Sponsor: this.props.Sponsor,
      City: this.props.City,
      Country: this.props.Country,
      Dates: this.props.Dates,
      Purpose: this.props.Purpose,
      Contacts: this.props.Contacts,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateDescription = (values: ForeignBusinessConferencesItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateSponsor = (values: ForeignBusinessConferencesItemModel['Sponsor']) => {
    this.update({
      Sponsor: values,
    });
  };

  updateCity = (values: ForeignBusinessConferencesItemModel['City']) => {
    this.update({
      City: values,
    });
  };

  updateCountry = (values: ForeignBusinessConferencesItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateDates = (values: ForeignBusinessConferencesItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updatePurpose = (values: ForeignBusinessConferencesItemModel['Purpose']) => {
    this.update({
      Purpose: values,
    });
  };

  updateContacts = (values: ForeignBusinessConferencesItemModel['Contacts']) => {
    this.update({
      Contacts: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="conferences-item">
        <Field
          attribute={`${attribute}.Description`}
          title={i18n.t('foreign.business.conferences.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            className="conferences-description"
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Field
          attribute={`${attribute}.Sponsor`}
          title={i18n.t('foreign.business.conferences.heading.sponsor')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.Sponsor`}
            name="Sponsor"
            {...this.props.Sponsor}
            onUpdate={this.updateSponsor}
            onError={this.props.onError}
            className="conferences-sponsor"
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.City`}
          title={i18n.t('foreign.business.conferences.heading.city')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.City`}
            name="City"
            {...this.props.City}
            onUpdate={this.updateCity}
            onError={this.props.onError}
            pattern={'^[0-9A-Za-z.,()!\' -]*$'}
            inputFilter={/[^0-9A-Za-z.,()!' -]/g}
            className="conferences-city"
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Country`}
          title={i18n.t('foreign.business.conferences.heading.country')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            attribute={`${attribute}.Country`}
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            className="conferences-country"
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Dates`}
          title={i18n.t('foreign.business.conferences.heading.dates')}
          help="foreign.business.conferences.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            className="conferences-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.Purpose`}
          title={i18n.t('foreign.business.conferences.heading.purpose')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Purpose`}
            name="Purpose"
            {...this.props.Purpose}
            onUpdate={this.updatePurpose}
            onError={this.props.onError}
            className="conferences-purpose"
            required={this.props.required}
          />
        </Field>

        <ConferenceContacts
          attribute={`${attribute}.Contacts`}
          name="Contacts"
          {...this.props.Contacts}
          onUpdate={this.updateContacts}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
      </div>
    );
  }
}

ConferencesItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  CountryFocused: false,
};
